import { includes, omit } from 'lodash-es'
import icoLookup from '@/utilities/ico-lookup'
import A from '@/utilities/alpha'
import * as graph from '@shared/types/graphql'
import * as db from '@shared/types/database'
import api from '@/api'
import S3Assets from '@/api/S3Assets'

export type dbProtoTypeRecord = Record<string, db.Prototype>

export function thumbnail (asset: graph.Prototype): string {
  if (asset.img) {
    return asset.img
  } else {
    return asset.assetType ? icoLookup[asset.assetType] : thumbnailFromURL(asset.url ?? '')
  }
}
// If url is an image return that, otherwise return a generic upload
// graphic
export function thumbnailFromURL (url: string): string {
  let img = '/img/asset_icons/upload.svg';
  ['png', 'jpg', 'jpeg', 'gif'].forEach(ext => {
    if (url.slice((ext.length * -1)).toLowerCase() === ext) {
      img = url
    }
  })
  return img
}

export function ensureProtocol (url: string): string {
  if (url.slice(0, 7) !== 'http://' && url.slice(0, 8) !== 'https://') return 'http://' + url
  return url
}

export async function upload (file: File) {
  const key = genAWSKey(file)
  await api.uploadFile(file, key)
  return await formatAsset(file, key)
}

function genAWSKey (file: File): string {
  return ((Math.random().toString(36)).slice(2) +
    (Math.random().toString(36)).slice(2)).slice(3, 19) +
    fileExtension(file)
}

export function fileExtension (file: File): string {
  const parts = file.name.split('.')
  // Check if has an extension
  if (parts.length > 1) {
    const ext = parts.pop()!
    // Validate extension (just check that its alphabetic)
    if (ext.match(/^[a-zA-Z]+$/)) {
      return '.' + ext
    }
  }
  return ''
}

export async function formatAsset (file: File, key: string, mode: string = '') {
  const url = await S3Assets.generateAssetUrl(key)
  let assetType: db.AssetType = mode === 'modeData' ? '' : 'clientupload'

  if (includes(['image/jpeg', 'image/png', 'image/jpg'], file.type)) {
    assetType = ''
  }

  if (includes(['application/msword', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'], file.type)) {
    assetType = 'document'
  }

  if (includes(['audio/mpeg', 'audio/x-m4a'], file.type)) {
    assetType = 'audio'
  }

  if (includes(['video/mp4'], file.type)) {
    assetType = 'video'
  }

  return {
    asset_type: assetType,
    title: file.name.replace(/\.[^/.]+$/, ''),
    url: url
  }
}

export function ensureAssetImg (asset: graph.Prototype) {
  asset = A.clone(asset)
  if (asset.url?.match(/(\.png$|\.gif$|\.jpg$|\.jpeg$)/i)) {
    asset.img = asset.url
  } else if (asset.assetType && icoLookup[asset.assetType]) {
    asset.img = icoLookup[asset.assetType]
  } else {
    asset.img = '/img/asset_icons/upload.svg'
  }
  return asset
}

export function sharedAssetsFromWorkspace (workspace: graph.WorkspaceWithTests, projectId: string | null) {
  function addAssetToShared (asset: graph.Prototype, allShared: Record<string, graph.Prototype>, uniqueAssetUrls: Record<string, string>) {
    if (!asset.url) return
    if (uniqueAssetUrls[asset.url]) return
    if (asset.assetType === 'textComparison') return
    allShared[asset.id] = asset
    uniqueAssetUrls[asset.url] = asset.url
  }

  const allShared: Record<string, graph.Prototype> = {}
  const uniqueAssetUrls: Record<string, string> = {}

  // Test assets
  workspace.tests.forEach(test => {
    test.prototypes.forEach(asset => {
      addAssetToShared(asset, allShared, uniqueAssetUrls)
    })
  })

  // Project assets
  workspace.projects.forEach(project => {
    if (!projectId) return
    if (!project.ids.includes(projectId)) return
    project.prototypes.forEach(asset => {
      addAssetToShared(asset, allShared, uniqueAssetUrls)
    })
  })

  return allShared
}

// WARNING: This is only meant to be used for uploads without prototype flags. (conceptPrototype, comparisonPrototype etc)
export function formatDbPrototypes (prototypes: db.TestPayload['prototypes']): graph.Prototype[] {
  return Object.keys(prototypes).map(key => {
    const upload = prototypes[key]
    return {
      id: key,
      assetType: upload.asset_type,
      url: upload.url,
      title: upload.title
    }
  })
}

export function formatGraphPrototypes (prototypes: graph.Prototype[]): dbProtoTypeRecord {
  return prototypes.reduce((acc, prototype) => {
    acc[prototype.id] = {
      asset_type: prototype.assetType as db.AssetType,
      url: prototype.url!,
      title: prototype.title!
    }
    return acc
  }, {} as Record<string, db.Prototype>)
}

export function editUpload (editableUploads: dbProtoTypeRecord, upload: graph.Prototype): dbProtoTypeRecord {
  for (const i in editableUploads) {
    if (i === upload.id) {
      const formattedUpload = formatGraphPrototypes([upload])
      editableUploads[i] = formattedUpload[i]
      break
    }
  }
  return editableUploads
}

export function addUploads (editableUploads: dbProtoTypeRecord, uploads: dbProtoTypeRecord): dbProtoTypeRecord {
  editableUploads = { ...editableUploads, ...uploads }
  return editableUploads
}

export function removeUpload (editableUploads: dbProtoTypeRecord, assetKey: string): dbProtoTypeRecord {
  editableUploads = omit(editableUploads, assetKey)
  return editableUploads
}

export const removeSignature = (url: string) => {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.delete('Expires')
    urlObj.searchParams.delete('Policy')
    urlObj.searchParams.delete('Signature')
    urlObj.searchParams.delete('Key-Pair-Id')
    return urlObj.toString()
  } catch (error) {
    return undefined
  }
}
